// Period + comparison selector: two halves of one rounded control. The left
// picks the window being reported on, the right picks what it's measured
// against - both show the resolved dates underneath so the numbers are never
// ambiguous about which days they cover.
import { useT } from '@/i18n';
import type { ComparisonBasis } from '@restaurant/api-client';
import { Actions, ActionsButton, ActionsGroup, ActionsLabel } from 'framework7-react';
import { useState } from 'react';
import { formatRangeLabel, RANGE_PRESETS, type DateRange, type RangePreset } from '../lib/range';

const MUTED = '#8e8e93';

const PRESET_KEY: Record<RangePreset, 'today' | 'yesterday' | 'thisWeek' | 'last7' | 'thisMonth'> =
  {
    TODAY: 'today',
    YESTERDAY: 'yesterday',
    THIS_WEEK: 'thisWeek',
    LAST_7: 'last7',
    THIS_MONTH: 'thisMonth',
  };

const BASIS_KEY: Record<ComparisonBasis, 'previousPeriod' | 'samePeriodLastYear'> = {
  PREVIOUS_PERIOD: 'previousPeriod',
  SAME_PERIOD_LAST_YEAR: 'samePeriodLastYear',
};

const BASES: ComparisonBasis[] = ['PREVIOUS_PERIOD', 'SAME_PERIOD_LAST_YEAR'];

/** Action-sheet option label with a trailing check on the active choice. */
function Chosen({ active, children }: { active: boolean; children: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {children}
      {active && (
        <i className="f7-icons" style={{ fontSize: 16 }}>
          checkmark_alt
        </i>
      )}
    </span>
  );
}

function Half({
  caption,
  dot,
  title,
  subtitle,
  onClick,
  align,
}: {
  caption: string;
  dot: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
  align: 'left' | 'right';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: '10px 12px',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        borderRadius: align === 'left' ? '14px 0 0 14px' : '0 14px 14px 0',
      }}
    >
      <span style={{ color: MUTED, fontSize: 11, lineHeight: 1.2 }}>{caption}</span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: dot,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        <i className="f7-icons" style={{ fontSize: 13, color: MUTED, flexShrink: 0 }}>
          chevron_down
        </i>
      </span>
      {subtitle && (
        <span style={{ color: MUTED, fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {subtitle}
        </span>
      )}
    </button>
  );
}

export function PeriodBar({
  preset,
  onPresetChange,
  basis,
  onBasisChange,
  currentRange,
  comparisonRange,
  locale,
}: {
  preset: RangePreset;
  onPresetChange: (preset: RangePreset) => void;
  basis: ComparisonBasis;
  onBasisChange: (basis: ComparisonBasis) => void;
  currentRange: DateRange;
  comparisonRange: DateRange | null;
  locale: string;
}) {
  const t = useT();
  const ps = t.reports.periodSelector;
  const [periodSheet, setPeriodSheet] = useState(false);
  const [basisSheet, setBasisSheet] = useState(false);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          marginTop: 16,
          background: 'var(--f7-card-bg-color,#fff)',
          borderRadius: 14,
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <Half
          align="left"
          caption={ps.current}
          dot="var(--f7-theme-color,#007aff)"
          title={t.reports.rangePresets[PRESET_KEY[preset]]}
          subtitle={formatRangeLabel(currentRange, locale)}
          onClick={() => setPeriodSheet(true)}
        />
        <div style={{ width: 1, background: 'rgba(142,142,147,0.25)' }} />
        <Half
          align="right"
          caption={ps.compare}
          dot="#c7c7cc"
          title={ps.basis[BASIS_KEY[basis]]}
          subtitle={comparisonRange ? formatRangeLabel(comparisonRange, locale) : undefined}
          onClick={() => setBasisSheet(true)}
        />
      </div>

      <Actions opened={periodSheet} onActionsClosed={() => setPeriodSheet(false)}>
        <ActionsGroup>
          <ActionsLabel>{ps.current}</ActionsLabel>
          {RANGE_PRESETS.map((p) => (
            <ActionsButton key={p} onClick={() => onPresetChange(p)}>
              <Chosen active={p === preset}>{t.reports.rangePresets[PRESET_KEY[p]]}</Chosen>
            </ActionsButton>
          ))}
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton strong>{t.cancel}</ActionsButton>
        </ActionsGroup>
      </Actions>

      <Actions opened={basisSheet} onActionsClosed={() => setBasisSheet(false)}>
        <ActionsGroup>
          <ActionsLabel>{ps.compare}</ActionsLabel>
          {BASES.map((b) => (
            <ActionsButton key={b} onClick={() => onBasisChange(b)}>
              <Chosen active={b === basis}>{ps.basis[BASIS_KEY[b]]}</Chosen>
            </ActionsButton>
          ))}
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton strong>{t.cancel}</ActionsButton>
        </ActionsGroup>
      </Actions>
    </>
  );
}
